import type { NodeProps } from '@xyflow/react';
import { Handle, Position } from '@xyflow/react';
import { Activity, CreditCard, Eye, FileUp, Landmark, PenLine, Search, Shield } from 'lucide-react';
import { memo } from 'react';

import { cn } from '@/lib/utils';
import type { StepNodeType, WorkflowNode } from '../../../types';

const STEP_ICONS: Record<StepNodeType, React.ElementType> = {
  pan_verification: CreditCard,
  aadhaar_verification: Shield,
  face_match: Eye,
  liveness: Activity,
  bank_account_verification: Landmark,
  kra_check: Search,
  document_upload: FileUp,
  esign_agreement: PenLine,
};

const STEP_COLORS: Record<StepNodeType, string> = {
  pan_verification: 'bg-blue-50 border-blue-200 text-blue-700',
  aadhaar_verification: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  face_match: 'bg-violet-50 border-violet-200 text-violet-700',
  liveness: 'bg-purple-50 border-purple-200 text-purple-700',
  bank_account_verification: 'bg-green-50 border-green-200 text-green-700',
  kra_check: 'bg-amber-50 border-amber-200 text-amber-700',
  document_upload: 'bg-orange-50 border-orange-200 text-orange-700',
  esign_agreement: 'bg-rose-50 border-rose-200 text-rose-700',
};

export const BaseNode = memo(function BaseNode({ data, selected }: NodeProps<WorkflowNode>) {
  const Icon = STEP_ICONS[data.stepType];
  const colorClass = STEP_COLORS[data.stepType];

  return (
    <div
      className={cn(
        'w-44 rounded-lg border-2 bg-white shadow-sm transition-shadow',
        selected ? 'border-primary shadow-md' : 'border-placeholder/60 hover:border-primary/50',
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white !bg-primary"
      />

      <div className="px-3 py-3 flex items-center gap-2.5">
        <div
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center border',
            colorClass,
          )}
        >
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs font-medium text-gray30 leading-tight">{data.label}</span>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !border-white !bg-primary"
      />
    </div>
  );
});
